import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from './api-client';
import type { LeaveDocumentDto, LeaveSigningStepDto, TimeOffRequestDto } from './types';

/** The prefix of every leave-document read, so one invalidation reaches all of them. */
const LEAVE_DOCUMENTS_KEY = ['leave-documents'] as const;

/** How long an unsigned leave form waits, the closing sentence of every toast about one (decision A19). */
const UNSIGNED_FORMS_DISCARDED = 'Unsigned forms are discarded after 24 hours.';

/** Where a requester whose leave form did not open finds it again: the To Sign list of their Documents tab. */
export const UNSIGNED_REQUEST_MESSAGE =
  `Request saved. Sign its leave form under My Info > Documents > To Sign to send it for approval. ${UNSIGNED_FORMS_DISCARDED}`;

/** The same for the requester's own change of approved leave whose leave form did not open. */
export const UNSIGNED_CHANGE_MESSAGE =
  `Change saved. Sign its leave form under My Info > Documents > To Sign to send it for approval. ${UNSIGNED_FORMS_DISCARDED}`;

/** The same for the requester's own cancellation of leave in progress whose new leave form did not open. */
export const UNSIGNED_CANCELLATION_MESSAGE =
  `Cancellation saved. Sign its leave form under My Info > Documents > To Sign to send it for approval. ${UNSIGNED_FORMS_DISCARDED}`;

/** The note field of a request whose leave form prints the note as the reason for absence. */
export const LEAVE_FORM_NOTE_LABEL = 'Reason for absence (printed on the leave form)';

/** True when leave at the given location needs a signed leave request form. */
export function useLeaveFormRequired(locationId: number | null | undefined): boolean {
  const locations = useLeaveDocumentLocations();
  const enabledIds = Array.isArray(locations.data) ? locations.data : [];
  return locationId != null && enabledIds.includes(locationId);
}

/** The ids of the locations whose leave needs a signed leave request form. */
function useLeaveDocumentLocations() {
  return useQuery({
    queryKey: [...LEAVE_DOCUMENTS_KEY, 'enabled-locations'],
    queryFn: () => api.get<number[]>('/v1/leave-documents/enabled-locations'),
  });
}

/** Every round of the employee's leave request forms the viewer may see, newest first. */
export function useEmployeeLeaveDocuments(employeeId: number) {
  return useQuery({
    queryKey: [...LEAVE_DOCUMENTS_KEY, 'employee', employeeId],
    queryFn: () => api.get<LeaveDocumentDto[]>(`/v1/leave-documents/employees/${employeeId}`),
  });
}

/** Every version of one request's leave request form the viewer may see, oldest first. */
export function useRequestLeaveDocuments(requestId: number) {
  return useQuery({
    queryKey: [...LEAVE_DOCUMENTS_KEY, 'request', requestId],
    queryFn: () => api.get<LeaveDocumentDto[]>(`/v1/leave-documents/requests/${requestId}`),
  });
}

/**
 * The leave request form a signing document is and the role its signer plays there, read only for a
 * document of a leave request form; answered to its signer and to admins (404 for anyone else).
 */
export function useLeaveFormOfDocument(documentId: number, isLeaveForm: boolean) {
  return useQuery({
    queryKey: [...LEAVE_DOCUMENTS_KEY, 'form', documentId],
    queryFn: () => api.get<LeaveSigningStepDto>(`/v1/leave-documents/forms/${documentId}`),
    enabled: isLeaveForm,
  });
}

/** True for the answer a leave-document read gives below its floor, or when it has nothing to show. */
export function isHiddenFromViewer(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 403 || err.status === 404);
}

/** Marks every leave-document read stale, after anything that may start, move or end a round. */
export function invalidateLeaveDocuments(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: LEAVE_DOCUMENTS_KEY });
}

/** True for a request whose leave form waits for the signature of the person who just submitted or edited it. */
export function awaitsOwnSignature(request: TimeOffRequestDto, onBehalf: boolean): boolean {
  return !onBehalf && request.signingStage === 'EMPLOYEE';
}

/** What someone who submitted a request on the employee's behalf is told: the employee signs it first (D6). */
export function onBehalfRequestMessage(employeeName: string): string {
  return `Request created. ${employeeName} must sign it before it is sent for approval. ${UNSIGNED_FORMS_DISCARDED}`;
}

/** What someone who changed an approved leave on the employee's behalf is told: the employee signs it first. */
export function onBehalfChangeMessage(employeeName: string): string {
  return `Change saved. ${employeeName} must sign it before it is sent for approval. ${UNSIGNED_FORMS_DISCARDED}`;
}

/**
 * What someone who cancelled leave in progress on the employee's behalf is told: the employee signs the
 * new leave form for the days already taken first.
 */
export function onBehalfCancellationMessage(employeeName: string): string {
  return `A new leave request form was sent to ${employeeName} to sign. The cancellation takes effect once it is approved and signed. ${UNSIGNED_FORMS_DISCARDED}`;
}

/**
 * Opens the sign page of the request's leave form waiting for the requester, which returns to the
 * page it was opened from. Resolves false when the form cannot be read, leaving the caller on its page.
 */
export function useOpenLeaveForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const returnTo = useCurrentPath();
  return useCallback(
    async (requestId: number) => {
      const step = await signingStepOf(requestId);
      if (!step) return false;
      markSigningListsStale(queryClient);
      void navigate(signPagePath(step.documentId, returnTo));
      return true;
    },
    [navigate, queryClient, returnTo],
  );
}

/** The in-app path of the current page, its query and fragment included. */
function useCurrentPath(): string {
  const location = useLocation();
  return `${location.pathname}${location.search}${location.hash}`;
}

/** The sign page of a document, which returns to the given in-app path once it is signed or declined. */
function signPagePath(documentId: number, returnTo: string): string {
  return `/sign/${documentId}?returnTo=${encodeURIComponent(returnTo)}`;
}

/** The request's leave form waiting for the requester, or null when it cannot be read. */
async function signingStepOf(requestId: number): Promise<LeaveSigningStepDto | null> {
  try {
    return await api.get<LeaveSigningStepDto>(`/v1/leave-documents/requests/${requestId}/signing`);
  } catch {
    return null;
  }
}

/** Marks stale the lists a form waiting for its signer appears in, so a page left unsigned finds it there. */
function markSigningListsStale(queryClient: QueryClient) {
  invalidateLeaveDocuments(queryClient);
  void queryClient.invalidateQueries({ queryKey: ['employee-to-sign-documents'] });
}
